import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useFormatting } from '../hooks/useFormatting';
import { PoliticianData, TimeframeData } from '../types';
import { generateSlug } from './SEOHead';
import './Leaderboard.css';

interface LeaderboardProps {
  data: TimeframeData;
}

type FilterParty = 'all' | 'Democrat' | 'Republican';
type FilterChamber = 'all' | 'House' | 'Senate';

const Leaderboard: React.FC<LeaderboardProps> = ({
  data,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { formatPercentage } = useFormatting();

  const [filterParty, setFilterParty] = useState<FilterParty>('all');
  const [filterChamber, setFilterChamber] = useState<FilterChamber>('all');

  // Filter and calculate monthly performance
  const { currentMonthTop, currentMonthBottom, monthLabel, totalCount } = useMemo(() => {
    let politicians = Object.values(data.politicians);

    // Apply filters
    if (filterParty !== 'all') {
      politicians = politicians.filter(p => p.party === filterParty);
    }
    if (filterChamber !== 'all') {
      politicians = politicians.filter(p => p.chamber === filterChamber);
    }

    // Find the last available date in the data to determine which month to use
    let lastDate = '';
    politicians.forEach(p => {
      if (p.data.length > 0) {
        const politicianLastDate = p.data[p.data.length - 1].date;
        if (politicianLastDate > lastDate) {
          lastDate = politicianLastDate;
        }
      }
    });

    // Get the month of the last available data
    const lastDataDate = lastDate ? new Date(lastDate) : new Date();

    // Calculate start of the current month in the data
    const currentMonthStart = new Date(lastDataDate.getFullYear(), lastDataDate.getMonth(), 1).toISOString().split('T')[0];

    // Calculate end of previous month (one day before current month start)
    const prevMonthEnd = new Date(lastDataDate.getFullYear(), lastDataDate.getMonth(), 0).toISOString().split('T')[0];

    // Format month label for display (show "Last 30 Days" or similar)
    const monthName = lastDataDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    const monthlyPerformance = politicians.map(p => {
      if (p.data.length < 2) {
        return { ...p, monthReturn: 0 };
      }

      const endValue = p.data[p.data.length - 1]?.value || p.currentValue;

      // Find the last data point from the previous month (or earliest this month)
      // This gives us the "starting point" to calculate returns from
      let startValue = endValue;

      // Find the last data point that's before or at the start of current month
      for (let i = p.data.length - 1; i >= 0; i--) {
        const pointDate = p.data[i].date.substring(0, 10); // Get YYYY-MM-DD
        if (pointDate <= prevMonthEnd) {
          startValue = p.data[i].value;
          break;
        }
        // If we're still in current month, use this as potential start
        if (pointDate < currentMonthStart) {
          startValue = p.data[i].value;
          break;
        }
      }

      // If no previous month data found, use the earliest data point in current month
      if (startValue === endValue && p.data.length > 1) {
        // Find first data point in current month
        for (let i = 0; i < p.data.length; i++) {
          const pointDate = p.data[i].date.substring(0, 10);
          if (pointDate >= currentMonthStart) {
            startValue = p.data[i].value;
            break;
          }
        }
      }

      const monthReturn = startValue > 0 ? ((endValue - startValue) / startValue) * 100 : 0;
      return { ...p, monthReturn };
    });

    const sortedByMonth = [...monthlyPerformance].sort((a, b) => b.monthReturn - a.monthReturn);
    const monthTop10 = sortedByMonth.slice(0, 10);
    // Bottom 10: keep in order from least bad to worst (no reverse)
    const monthBottom10 = sortedByMonth.slice(-10);

    return {
      currentMonthTop: monthTop10,
      currentMonthBottom: monthBottom10,
      monthLabel: monthName,
      totalCount: sortedByMonth.length,
    };
  }, [data.politicians, filterParty, filterChamber]);

  const handlePoliticianClick = (politician: PoliticianData) => {
    navigate(`/politician/${generateSlug(politician.name)}`);
  };

  const renderPerformerCard = (
    performer: PoliticianData & { monthReturn?: number },
    index: number,
    isTop: boolean
  ) => {
    const accentColor = isTop ? 'var(--accent-gold)' : 'var(--accent-red)';
    const returnValue = performer.monthReturn ?? 0;

    // For top performers: #1, #2, #3...
    // For bottom performers: #161, #162... #170 (index 0 = least bad, index 9 = worst)
    const rank = isTop ? index + 1 : totalCount - currentMonthBottom.length + index + 1;

    return (
      <button
        key={performer.id}
        className={`leaderboard-card ${isTop ? 'top' : 'bottom'}`}
        onClick={() => handlePoliticianClick(performer)}
        style={{
          '--accent-color': accentColor,
          animationDelay: `${index * 0.03}s`,
        } as React.CSSProperties}
      >
        <div className={`leaderboard-rank ${isTop ? 'top' : 'bottom'}`}>
          {`#${rank}`}
        </div>

        <div className="leaderboard-avatar">
          <img
            src={`/images/${performer.id}.jpg`}
            alt={performer.name}
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/images/placeholder.png';
            }}
          />
          <div
            className="avatar-ring"
            style={{ borderColor: accentColor }}
          />
        </div>

        <div className="leaderboard-info">
          <span className="leaderboard-name">{performer.name}</span>
          <span className="leaderboard-meta">
            <span className={`party-badge ${performer.party === 'Democrat' ? 'dem' : 'rep'}`}>
              {performer.party === 'Democrat' ? 'D' : 'R'}
            </span>
            {performer.state} · {performer.chamber}
          </span>
        </div>

        <div className="leaderboard-stats">
          <span className={`leaderboard-return ${returnValue >= 0 ? 'positive' : 'negative'}`}>
            {formatPercentage(returnValue)}
          </span>
        </div>

        <div
          className="leaderboard-indicator"
          style={{ backgroundColor: accentColor }}
        />
      </button>
    );
  };

  return (
    <div className="leaderboard-page">
      <header className="leaderboard-header">
        <div className="leaderboard-header-left">
          <h1 className="leaderboard-title" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            {t('appTitle')}
          </h1>
          <h2 className="leaderboard-subtitle">{t('leaderboard:title')}</h2>
        </div>
        <div className="leaderboard-header-right">
          <div className="leaderboard-filters">
            <select
              className="filter-select"
              value={filterParty}
              onChange={(e) => setFilterParty(e.target.value as FilterParty)}
            >
              <option value="all">{t('leaderboard:allParties')}</option>
              <option value="Democrat">{t('politician:democrat')}</option>
              <option value="Republican">{t('politician:republican')}</option>
            </select>
            <select
              className="filter-select"
              value={filterChamber}
              onChange={(e) => setFilterChamber(e.target.value as FilterChamber)}
            >
              <option value="all">{t('leaderboard:allChambers')}</option>
              <option value="House">{t('politician:house')}</option>
              <option value="Senate">{t('politician:senate')}</option>
            </select>
          </div>
        </div>
      </header>

      <main className="leaderboard-main">
        {/* Monthly Performance Section */}
        <section className="leaderboard-section">
          <h3 className="section-title">
            <span className="section-icon month">&#128197;</span>
            {monthLabel}
          </h3>
          <div className="leaderboard-columns">
            <div className="leaderboard-column">
              <div className="column-header top">
                <span className="column-icon">&#9650;</span>
                {t('leaderboard:topGainers')}
              </div>
              <div className="leaderboard-list">
                {currentMonthTop.map((performer, index) =>
                  renderPerformerCard(performer, index, true)
                )}
              </div>
            </div>
            <div className="leaderboard-column">
              <div className="column-header bottom">
                <span className="column-icon">&#9660;</span>
                {t('leaderboard:topLosers')}
              </div>
              <div className="leaderboard-list">
                {currentMonthBottom.map((performer, index) =>
                  renderPerformerCard(performer, index, false)
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="leaderboard-footer">
        <button className="back-to-dashboard" onClick={() => navigate('/')}>
          {t('leaderboard:backToDashboard')}
        </button>
      </footer>
    </div>
  );
};

export default Leaderboard;
